/**
 * Digital Staff WS chat client.
 *
 * chatbot-api owns service-token use, agent lifecycle, WebSocket proxying, and
 * conversation history persistence. The browser opens only chatbot-api's WS.
 */
import { apiClient } from '../../core/api';
import { env } from '../../core/env';
import { getDevLane } from '../../core/lane';
import type { ChatMessage, ConversationMeta } from '../../../types/agentChat';
import { frameToEvent } from './ws-chat/protocol';
import {
  initialWsChatState,
  wsChatReducer,
  type WsChatApprovalDecision,
  type WsChatRunState,
  type WsChatTurnTrace,
} from './ws-chat/runtime';

interface BootstrapResp {
  agentId: string;
  ready?: boolean;
}

export interface WsChatSession {
  userId: string;
  agentId: string;
  wsUrl: string;
}

interface WsChatSessionDTO {
  id: string;
  startedAt?: string;
  lastActivityAt?: string;
  lastMessagePreview?: string;
  userDisplayName?: string;
  /** 一句概括整段对话的标题，后端后台补写；空表示还没生成。 */
  summary?: string | null;
  /** YEP-805: the server's own unread verdict for this row. */
  unread?: boolean;
}

/**
 * Everything the sidebar needs to draw its chat dots, in one call (YEP-805).
 *
 * `byAgent` counts unread conversations per agent_type — that lights the agent row and, unioned,
 * the AI TEAM aggregate. `byConversation` is the id set behind those counts, which is what lets a
 * caller subtract the conversation currently on screen without double-counting.
 */
export interface ChatUnreadSummary {
  byAgent: Record<string, number>;
  byConversation: Record<string, boolean>;
  total: number;
}

interface WsChatSessionEventDTO {
  direction?: string;
  type?: string;
  payload?: unknown;
  timestamp?: string;
}

interface WsChatMessageDTO {
  id?: string;
  role?: string;
  sender?: string;
  author?: string;
  direction?: string;
  content?: unknown;
  text?: unknown;
  message?: unknown;
  timestamp?: string | number;
  createdAt?: string | number;
}

interface WsChatConversationDetailDTO {
  events?: WsChatSessionEventDTO[];
  messages?: WsChatMessageDTO[];
}

const CHAT_TOKEN_CACHE_TTL_MS = 55 * 60 * 1000;

// One entry per `${userId}::${agentType}` — alternating between agents on one
// socket must not evict the other agent's still-fresh session.
const cachedSessions = new Map<string, { session: WsChatSession; cachedAt: number }>();

export async function bootstrapWsChat(
  userId: string | undefined,
  agentType: string,
  force = false
): Promise<WsChatSession> {
  if (!userId) throw new Error('No user available for WS chat');
  const key = `${userId}::${agentType}`;
  const cached = cachedSessions.get(key);
  if (cached && !force && Date.now() - cached.cachedAt < CHAT_TOKEN_CACHE_TTL_MS) {
    return cached.session;
  }

  const resp = await apiClient.chatbotGet<BootstrapResp>(
    `/digital-staff/bootstrap/${encodeURIComponent(userId)}?agentType=${encodeURIComponent(agentType)}`
  );
  const data = resp.data;
  if (!data?.agentId) {
    // The api client converts backend error responses into a resolved 200 with
    // an {errorCode, errorMessage} body — surface that instead of a generic error.
    const upstreamMessage = (data as { errorMessage?: string } | undefined)?.errorMessage;
    throw new Error(
      upstreamMessage
        ? `Digital Staff bootstrap failed: ${upstreamMessage}`
        : 'Digital Staff bootstrap returned incomplete WS chat data'
    );
  }
  const session = {
    userId,
    agentId: data.agentId,
    wsUrl: buildChatbotWsBaseUrl(userId, agentType),
  };
  cachedSessions.set(key, { session, cachedAt: Date.now() });
  return session;
}

export function clearWsChatSession(): void {
  cachedSessions.clear();
}

function toArray(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (
    body &&
    typeof body === 'object' &&
    Array.isArray((body as { content?: unknown[] }).content)
  ) {
    return (body as { content: unknown[] }).content;
  }
  return [];
}

/** How many conversations the rail reads. It shows a handful; a test merchant has hundreds, and
 *  the unbounded list (280 KB) took 29 s through the dev gateway under load — past the client's
 *  10 s timeout, so the rail said "No recent chats yet" over a history that was there. */
export const CONVERSATION_LIST_LIMIT = 200;
export const CONVERSATION_LIST_TIMEOUT_MS = 60_000;

export async function listConversations(
  userId?: string,
  agentType?: string,
  options?: { limit?: number; timeoutMs?: number }
): Promise<ConversationMeta[]> {
  if (!userId) return [];
  const params = new URLSearchParams();
  if (agentType) params.set('agentType', agentType);
  const limit = options?.limit ?? CONVERSATION_LIST_LIMIT;
  if (limit > 0) params.set('limit', String(limit));
  const q = params.toString() ? `?${params.toString()}` : '';
  const resp = await apiClient.chatbotGet<unknown>(
    `/digital-staff/conversations/${encodeURIComponent(userId)}${q}`,
    undefined,
    { timeout: options?.timeoutMs ?? CONVERSATION_LIST_TIMEOUT_MS }
  );
  const rows = toArray(resp.data) as WsChatSessionDTO[];
  return rows
    .filter(r => r && r.id)
    .map(r => ({
      conversationId: r.id,
      // 三层，从最像「这段对话叫什么」到最不像：
      //   summary  —— 一句概括，后端后台补写（懒加载，先有的行先有）
      //   userDisplayName —— 后端的 title：商家第一条有实质内容的消息
      //   lastMessagePreview —— 最后一句，每条消息都会覆盖它
      // preview 曾经排在最前面，行名因此随商家最后一句漂移——QA 2026-09-14
      // 看到的「已完成」就是这么来的。
      title: (r.summary || r.userDisplayName || r.lastMessagePreview || 'Conversation').slice(0, 60),
      updatedAt: r.lastActivityAt || r.startedAt,
      // YEP-805: unread is the SERVER's answer, not a client watermark. The same query feeds
      // `getChatUnreadSummary`, so a row and the sidebar aggregate cannot disagree.
      unread: r.unread === true,
    }))
    .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
}

/**
 * Fired after a read has been STAMPED, so anything holding a conversation list can catch up.
 *
 * <p>Without it the merchant switching from chat A to chat B relights A: the list in hand still
 * carries the `unread:true` it was fetched with, and nothing refetches on a switch — `useWsChat`
 * only refreshes when a turn completes. The dot would come back for up to a poll cycle on the most
 * ordinary interaction there is.
 *
 * <p>Carries WHICH conversation was read, because a refetch triggered by it can still come back
 * stale: `apiClient.chatbotGet` de-duplicates identical in-flight GETs, so a list request that was
 * already on the wire when the read landed is handed back verbatim — pre-stamp. Listeners use the id
 * to notice that and ask once more.
 */
export const CHAT_READ_EVENT = 'yepai:chat-read';

/** The payload of {@link CHAT_READ_EVENT}. */
export interface ChatReadEventDetail {
  conversationId: string;
}

/**
 * The merchant opened this conversation, so it is read from now on (YEP-805).
 *
 * <p>Idempotent server-side, and silent on an id this merchant does not own — so a stale sidebar row
 * reporting a deleted conversation is not an error worth surfacing. Callers therefore fire it and
 * forget: a failed report costs a dot that clears on the next open, never a broken chat.
 *
 * <p>The event is dispatched only after the POST resolves: the backend stamps `last_read_at` inside
 * that request, so a refetch triggered any earlier would read the row back as still unread.
 */
export async function markConversationRead(
  userId: string | undefined,
  conversationId: string,
  agentType?: string
): Promise<void> {
  if (!userId || !conversationId) return;
  const q = agentType ? `?agentType=${encodeURIComponent(agentType)}` : '';
  const resp = await apiClient.chatbotPost(
    `/digital-staff/conversations/${encodeURIComponent(userId)}/${encodeURIComponent(conversationId)}/read${q}`
  );
  // `apiClient` resolves a non-401 HTTP error as a 200 carrying {errorCode, errorMessage}, so
  // without this a FAILED read announces itself as a successful one — every listener refetches,
  // sees the row still unread, and the caller's `.catch` never runs. Throw, so the failure is a
  // failure.
  // `typeof` first: this endpoint answers a bare `true`/`false`, and `in` throws on a primitive.
  const body = resp.data as unknown;
  if (!!body && typeof body === 'object' && 'errorCode' in body && 'errorMessage' in body) {
    throw new Error(
      (body as { errorMessage?: string }).errorMessage || 'Could not mark the conversation read'
    );
  }
  window.dispatchEvent(
    new CustomEvent<ChatReadEventDetail>(CHAT_READ_EVENT, { detail: { conversationId } })
  );
}

/** Unread chats across every agent, for the sidebar's dots (YEP-805). */
export async function getChatUnreadSummary(userId?: string): Promise<ChatUnreadSummary> {
  if (!userId) return { byAgent: {}, byConversation: {}, total: 0 };
  const resp = await apiClient.chatbotGet<ChatUnreadSummary>(
    `/digital-staff/unread/${encodeURIComponent(userId)}`
  );
  const body = resp.data as
    (ChatUnreadSummary & { errorCode?: number; errorMessage?: string }) | null;
  // `apiClient` resolves a non-401 HTTP error as a 200 carrying {errorCode, errorMessage}. Falling
  // back to an empty summary here would read as "nothing unread" and clear every chat dot for the
  // length of an outage; throw so the caller's failure path keeps the last known answer.
  if (body && typeof body === 'object' && 'errorCode' in body && 'errorMessage' in body) {
    throw new Error(body.errorMessage || 'Could not load unread summary');
  }
  return {
    byAgent: body?.byAgent ?? {},
    byConversation: body?.byConversation ?? {},
    total: body?.total ?? 0,
  };
}

/** One recorded frame, folded into the replayed conversation state. */
function replayRow(
  state: WsChatRunState,
  row: WsChatSessionEventDTO,
  payload: Record<string, unknown>
): WsChatRunState {
  if (row.direction === 'client') {
    if (payload.type === 'message' && typeof payload.content === 'string') {
      return wsChatReducer(state, { type: 'user-message', text: payload.content });
    }
    if (
      payload.type === 'approval_response' &&
      typeof payload.request_id === 'string' &&
      isApprovalDecision(payload.decision)
    ) {
      return wsChatReducer(state, {
        type: 'approval-decided',
        id: payload.request_id,
        decision: payload.decision,
      });
    }
    return state;
  }
  const event = frameToEvent(payload);
  return event ? wsChatReducer(state, event) : state;
}

export async function getConversationDetail(
  userId: string | undefined,
  conversationId: string,
  agentType?: string
): Promise<{ messages: ChatMessage[]; traces: Record<string, WsChatTurnTrace> }> {
  if (!userId) throw new Error('No user available for WS chat history');
  const q = agentType ? `?agentType=${encodeURIComponent(agentType)}` : '';
  const resp = await apiClient.chatbotGet<WsChatConversationDetailDTO>(
    `/digital-staff/conversations/${encodeURIComponent(userId)}/${encodeURIComponent(conversationId)}${q}`
  );
  const rows = Array.isArray(resp.data?.events) ? resp.data.events : [];
  if (rows.length === 0 && Array.isArray(resp.data?.messages)) {
    return {
      messages: resp.data.messages
        .map(rawHistoryMessageToChatMessage)
        .filter((m): m is ChatMessage => m != null)
        .map(m => ({ ...m, fromHistory: true })),
      traces: {},
    };
  }

  let state = initialWsChatState();

  rows.forEach(row => {
    const payload = toRecord(row.payload);
    if (!payload) return;
    const before = state.messages.length;
    state = replayRow(state, row, payload);
    // The reducer stamps every message it adds with Date.now() — right live, wrong here: the whole
    // history would read "Just now". Each row carries when the server recorded it; a message the
    // row added gets that time.
    const at = row.timestamp ? Date.parse(row.timestamp) : NaN;
    if (state.messages.length > before && !Number.isNaN(at)) {
      state = {
        ...state,
        messages: state.messages.map((m, i) => (i >= before ? { ...m, timestamp: at } : m)),
      };
    }
  });


  const hasIncompleteTrace =
    !!state.statusText?.trim() ||
    state.tools.length > 0 ||
    state.taskSteps.length > 0 ||
    !!state.streaming?.text.trim();
  const incompleteMessageId = state.streaming?.id ?? `wsChat-incomplete-${state.seq}`;
  const messages = hasIncompleteTrace
    ? [
        ...state.messages,
        {
          id: incompleteMessageId,
          role: 'assistant' as const,
          content: state.streaming?.text ?? '',
          timestamp: Date.now(),
          // The turn may still be running: a refresh mid-turn lands here, and the live turn
          // that resumes over the socket is this one. The reducer folds it back in on `resume`
          // (runtime.ts resumeHistoryTail) — otherwise this half and the live half are drawn
          // as two progress boxes of the same run, side by side, until the turn ends.
          turnInProgress: true,
        },
      ]
    : state.messages;
  const traces = hasIncompleteTrace
    ? {
        ...state.traces,
        [incompleteMessageId]: {
          thinking: state.statusText ?? undefined,
          tools: state.tools,
          // A turn cut short mid-walk keeps the steps it did reach: on reload the
          // merchant sees how far it got, not an empty card.
          taskSteps: state.taskSteps.length > 0 ? state.taskSteps : undefined,
        },
      }
    : state.traces;

  return {
    messages: messages.map(m => ({ ...m, fromHistory: true })),
    traces,
  };
}

export function buildChatWsUrl(
  s: WsChatSession,
  sessionId?: string | null,
  traceId?: string | null
): string {
  const url = new URL(s.wsUrl);
  if (sessionId) url.searchParams.set('sessionId', sessionId);
  if (traceId) url.searchParams.set('traceId', traceId);
  return url.toString();
}

export function createWsChatTraceId(): string {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `ws-${random}`;
}

export function buildChatbotWsBaseUrl(userId: string, agentType: string): string {
  const httpBase = env.apiBaseUrl || `${window.location.protocol}//${window.location.host}`;
  const wsBase = httpBase.replace(/^http/i, 'ws').replace(/\/$/, '');
  const lane = getDevLane();
  const laneQ = lane ? `&lane=${encodeURIComponent(lane)}` : '';
  return `${wsBase}/chatbot/v1/digital-staff/ws/${encodeURIComponent(userId)}?agentType=${encodeURIComponent(agentType)}${laneQ}`;
}

function toRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value === 'string') {
    try {
      const parsed: unknown = JSON.parse(value);
      return toRecord(parsed);
    } catch {
      return null;
    }
  }
  return null;
}

function rawHistoryMessageToChatMessage(raw: WsChatMessageDTO, index: number): ChatMessage | null {
  if (!raw || typeof raw !== 'object') return null;
  const role = historyRole(raw.role ?? raw.sender ?? raw.author ?? raw.direction);
  if (!role) return null;
  const content =
    historyString(raw.content) ?? historyString(raw.text) ?? historyString(raw.message);
  if (!content) return null;
  const rawTimestamp = raw.timestamp ?? raw.createdAt;
  const timestamp =
    typeof rawTimestamp === 'number'
      ? rawTimestamp
      : typeof rawTimestamp === 'string'
        ? Date.parse(rawTimestamp) || Date.now()
        : Date.now();
  return {
    id: raw.id || `wsChat-history-${index}`,
    role,
    content,
    timestamp,
  };
}

function historyRole(value: unknown): ChatMessage['role'] | null {
  if (typeof value !== 'string') return null;
  const normalized = value.toLowerCase();
  if (normalized === 'client' || normalized === 'human' || normalized.includes('user'))
    return 'user';
  if (
    normalized === 'upstream' ||
    normalized.includes('assistant') ||
    normalized.includes('agent') ||
    normalized.includes('bot')
  ) {
    return 'assistant';
  }
  return null;
}

function historyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function isApprovalDecision(value: unknown): value is WsChatApprovalDecision {
  return value === 'approve' || value === 'always' || value === 'deny';
}
